import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, EyeOff, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Stars } from "../gallery/Stars";
import { api, errMsg, imgUrl } from "../../lib/api";

const BADGE = { pending: "bg-[#D4AF37]/15 text-[#F3E5AB]", approved: "bg-[#25D366]/15 text-[#25D366]", hidden: "bg-[#ff8fb3]/15 text-[#ff8fb3]" };

export const ReviewList = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-reviews"], queryFn: () => api.get("/admin/reviews").then((r) => r.data) });
  const rows = Array.isArray(data) ? data : [];

  const act = async (fn, msg) => {
    try {
      await fn();
      qc.invalidateQueries({ queryKey: ["admin-reviews"] });
      qc.invalidateQueries({ queryKey: ["reviews"] });
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const setStatus = (id, status) => act(() => api.patch(`/admin/reviews/${id}`, { status }), status === "approved" ? "Review is now live" : "Review hidden");
  const del = (id) => window.confirm("Delete this review permanently?") && act(() => api.delete(`/admin/reviews/${id}`), "Review deleted");

  if (isLoading) return <Loader2 className="animate-spin text-[#F3E5AB]" />;
  if (!rows.length) return <p className="text-[#E8D8C8]" data-testid="reviews-empty">No reviews yet. Customers can write one from any category page.</p>;

  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="admin-reviews-list">
      {rows.map((r, i) => (
        <article key={r.id} className="flex gap-4 rounded-[20px] border border-[#D4AF37]/20 bg-[#330B2E]/70 p-4" data-testid={`admin-review-${i}`}>
          {r.photo_url && <img src={imgUrl(r.photo_url)} alt="" className="h-24 w-24 shrink-0 rounded-xl object-cover" />}
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="truncate font-display text-2xl">{r.name}</h3>
              <span className={`rounded-full px-3 py-1 text-xs capitalize ${BADGE[r.status]}`} data-testid={`admin-review-status-${i}`}>{r.status}</span>
            </div>
            <p className="text-xs text-[#F3E5AB]">{r.category_name} · {new Date(r.created_at).toLocaleDateString()}</p>
            <Stars value={r.rating} size={14} />
            <p className="text-sm text-[#E8D8C8]">{r.text}</p>
            <div className="flex flex-wrap gap-2 pt-1">
              {r.status !== "approved" && <button onClick={() => setStatus(r.id, "approved")} className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-[#06260f]" data-testid={`admin-review-approve-${i}`}><Check size={13} /> Approve</button>}
              {r.status !== "hidden" && <button onClick={() => setStatus(r.id, "hidden")} className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 px-3 py-1.5 text-xs text-[#F3E5AB]" data-testid={`admin-review-hide-${i}`}><EyeOff size={13} /> Hide</button>}
              <button onClick={() => del(r.id)} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-[#ff8fb3] hover:bg-[#240620]" data-testid={`admin-review-delete-${i}`}><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
};
